"use client";
import { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { 
  Cookie, 
  Settings, 
  Info, 
  Server, 
  Globe, 
  Check, 
  AlertTriangle, 
  Edit, 
  Mail,
  ChevronRight,
  Clock,
  Shield
} from "lucide-react";

export default function CookiesPage() {
  const [activeSection, setActiveSection] = useState<string | null>("what");
  const [isMobile, setIsMobile] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isScrollingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();

  // Cleanup function to be used in multiple places
  const cleanupAllEffects = () => {
    // Clean up observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    
    // Clear any pending timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Reset any ongoing scroll state
    isScrollingRef.current = false;
    
    // Remove any inline styles that might have been added to the html/body
    if (typeof document !== 'undefined') {
      document.documentElement.style.removeProperty('scroll-behavior');
      document.body.style.removeProperty('overflow');
    }
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    
    window.addEventListener("resize", checkMobile);
    
    // Call cleanup when component unmounts
    return () => {
      window.removeEventListener("resize", checkMobile);
      cleanupAllEffects();
    };
  }, []);

  // Listen for route changes to clean up
  useEffect(() => {
    // This will run when the component mounts
    return () => {
      // This will run when the component unmounts (route change)
      cleanupAllEffects();
    };
  }, [router]);

  // Setup intersection observer to detect active section on scroll
  useEffect(() => {
    // Clean up any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const options = {
      root: null,
      rootMargin: "-100px 0px -40% 0px",
      threshold: 0.2
    };

    observerRef.current = new IntersectionObserver((entries) => {
      // Don't update active section if user is currently clicking to scroll
      if (isScrollingRef.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, options);

    // Observe all section elements
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observerRef.current?.observe(ref);
    });

    // Cleanup on unmount or deps change
    return () => {
      cleanupAllEffects();
    };
  }, []);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = sectionRefs.current[id];
    if (!element) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a flag to prevent intersection observer from changing active section during programmatic scrolling
    isScrollingRef.current = true;

    // Use a more contained approach for scrolling to minimize global impact
    try {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      
      // Scroll only the specific element into view
      window.scrollTo({
        top: y,
        behavior: "smooth"
      });
    } catch (err) {
      console.error("Error scrolling to section:", err);
    }

    // Reset the flag after animation completes and store the timeout ref
    timeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      timeoutRef.current = null;
    }, 1000);
  };

  const sections = [
    {
      id: "what",
      title: "What Are Cookies",
      icon: Info,
      color: "bg-linear-to-r from-blue-500 to-indigo-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-5 shadow-sm border border-blue-100">
          <p className="mb-4 leading-relaxed text-gray-700">
            Cookies are small text files that are placed on your device when you visit our website. They allow us to recognize your device and remember certain information about your visit, such as your preferences and actions on our site.
          </p>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <div className="flex items-center gap-2 mb-2">
              <Cookie className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-gray-900">Cookie Basics</h3>
            </div>
            <p className="text-sm text-gray-700">
              Most websites use cookies to improve your browsing experience by remembering your preferences, enabling specific functions, and helping site owners understand how their site is being used.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "types",
      title: "Types of Cookies",
      icon: Server,
      color: "bg-linear-to-r from-purple-500 to-pink-600",
      content: (
        <div className="space-y-6">
          <div className="backdrop-blur-sm bg-white/80 rounded-xl p-5 shadow-sm border border-purple-100">
            <p className="text-gray-700 mb-4">
              We use different types of cookies to run our website and services. Each type serves a specific purpose to ensure optimal user experience.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                title: "Essential Cookies",
                description: "These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and account access. You cannot opt out of these cookies.",
                icon: Shield,
                color: "from-indigo-50 to-blue-50",
                textColor: "text-indigo-700"
              },
              {
                title: "Performance & Analytics",
                description: "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. They help us improve our website's functionality.",
                icon: Server,
                color: "from-purple-50 to-pink-50",
                textColor: "text-purple-700"
              },
              {
                title: "Functionality Cookies",
                description: "These cookies allow the website to remember choices you make (such as your username, language, or region) and provide enhanced, personalized features.",
                icon: Settings,
                color: "from-emerald-50 to-teal-50",
                textColor: "text-emerald-700"
              },
              {
                title: "Targeting & Advertising",
                description: "These cookies are used to track visitors across websites. They are used to display ads that are relevant and engaging for individual users.",
                icon: Globe,
                color: "from-amber-50 to-orange-50",
                textColor: "text-amber-700"
              }
            ].map((item, index) => (
              <div key={index} className={`bg-linear-to-br ${item.color} p-5 rounded-xl shadow-sm`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`p-2 rounded-full bg-white/70 ${item.textColor}`}>
                    <item.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-medium text-gray-900">{item.title}</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "third-party",
      title: "Third-Party Cookies",
      icon: Globe,
      color: "bg-linear-to-r from-amber-500 to-orange-600",
      content: (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <p className="text-gray-700 mb-4">
              In addition to our own cookies, we may also use various third-party cookies to report usage statistics of the service, deliver advertisements on and through the service, and so on.
            </p>
            
            <div className="bg-linear-to-br from-amber-50 to-orange-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Third-Party Services</h3>
              <div className="space-y-2">
                {[
                  "Analytics partners to understand how you use our website",
                  "Advertising providers to show you relevant ads across the web",
                  "Social media platforms when you use their share features",
                  "Support and chat services to help you communicate with us"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-600 shrink-0 mt-1" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="backdrop-blur-sm bg-white/70 rounded-xl p-5 shadow-sm border border-amber-100">
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 rounded-lg bg-amber-100 text-amber-700 mt-0.5">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">Important Notice</h3>
                <p className="text-gray-700">
                  Third-party cookies are controlled by their respective providers. We recommend reviewing the privacy policies of these third parties to understand how they use cookie information.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "managing",
      title: "Managing Cookies",
      icon: Settings,
      color: "bg-linear-to-r from-emerald-500 to-teal-600",
      content: (
        <div className="space-y-6">
          <div className="backdrop-blur-sm bg-white/80 rounded-xl p-5 shadow-sm border border-emerald-100">
            <p className="text-gray-700 mb-4">
              Most web browsers allow you to control cookies through their settings preferences. However, limiting cookies may affect the functionality of our website.
            </p>
            <p className="text-gray-700">
              You can typically remove or reject cookies via your browser settings. In order to do this, follow the instructions provided by your browser. Many browsers are set to accept cookies until you change your settings.
            </p>
          </div>
          
          <div className="bg-linear-to-br from-emerald-50 to-teal-50 p-5 rounded-xl">
            <h3 className="font-medium text-gray-900 mb-4">Browser Settings</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  browser: "Chrome",
                  instructions: "Settings → Privacy and Security → Cookies and other site data"
                },
                {
                  browser: "Firefox",
                  instructions: "Options → Privacy & Security → Cookies and Site Data"
                },
                {
                  browser: "Safari",
                  instructions: "Preferences → Privacy → Cookies and website data"
                },
                {
                  browser: "Edge",
                  instructions: "Settings → Site permissions → Cookies and site data"
                }
              ].map((item, index) => (
                <div key={index} className="bg-white/70 p-3 rounded-lg shadow-sm">
                  <h4 className="font-medium text-teal-800 mb-1">{item.browser}</h4>
                  <p className="text-sm text-gray-700">{item.instructions}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    {
      id: "changes",
      title: "Policy Changes",
      icon: Edit,
      color: "bg-linear-to-r from-blue-500 to-cyan-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-6 shadow-sm border border-blue-100">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-100 text-blue-700 mt-0.5">
              <Edit className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Updates to Our Cookie Policy</h3>
              <p className="text-gray-700">
                We may update our Cookie Policy from time to time. We will notify you of any changes by posting the new Cookie Policy on this page and updating the "Last Updated" date.
              </p>
            </div>
          </div>
          
          <div className="bg-linear-to-br from-blue-50 to-cyan-50 p-4 rounded-lg mt-4">
            <h3 className="font-medium text-blue-800 mb-2">How You'll Be Notified</h3>
            <ul className="text-gray-700 text-sm space-y-2">
              <li>• Updated "Last Modified" date at the top of this policy</li>
              <li>• Email notification to registered users for significant changes</li>
              <li>• Notice banner on our website highlighting major updates</li>
              <li>• Detailed changelog available upon request</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "contact",
      title: "Contact Us",
      icon: Mail,
      color: "bg-linear-to-r from-violet-500 to-fuchsia-600",
      content: (
        <div>
          <p className="mb-5 text-gray-700">
            If you have questions about our Cookie Policy or practices, please contact our Privacy Team at:
          </p>
          <div className="bg-linear-to-br from-violet-50 to-fuchsia-50 rounded-xl overflow-hidden shadow-sm">
            <div className="p-5 border-b border-violet-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-violet-100 text-violet-700">
                  <Mail className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-medium text-gray-900">Email</h3>
              </div>
              <p className="text-violet-700 font-medium">privacy@rentwise.com</p>
            </div>
            <div className="p-5 border-b border-fuchsia-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-fuchsia-100 text-fuchsia-700">
                  <Globe className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-medium text-gray-900">Address</h3>
              </div>
              <p className="text-gray-700">RentWise Privacy Office<br />123 Privacy Street<br />Security Town, ST 12345</p>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-purple-100 text-purple-700">
                  <Clock className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-medium text-gray-900">Response Time</h3>
              </div>
              <p className="text-gray-700">We strive to respond to all cookie and privacy inquiries within 48 hours.</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div id="cookies-page" className="min-h-screen relative bg-linear-to-b from-gray-50 to-white overflow-y-auto overflow-x-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-20 left-0 w-64 h-64 rounded-full bg-indigo-100/30 blur-3xl -z-10" />
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-amber-100/20 blur-3xl -z-10" />
      <div className="absolute top-1/3 right-0 w-72 h-72 rounded-full bg-teal-100/30 blur-3xl -z-10" />
      
      {/* Header */}
      <header className="pt-12 pb-6 md:pt-20 md:pb-12 container mx-auto px-4 md:px-6">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2, type: "spring" as const, stiffness: 100 }}
            className="mb-6 inline-flex"
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-amber-300 animate-ping opacity-20"></div>
              <div className="relative p-4 rounded-full bg-linear-to-r from-amber-500 to-orange-600 shadow-lg">
                <Cookie className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="text-4xl md:text-5xl font-bold bg-linear-to-r from-amber-600 to-orange-600 text-transparent bg-clip-text mb-4"
          >
            Cookie Policy
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="text-gray-600"
          >
            Last updated: April 23, 2025
          </motion.p>
        </motion.div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-6 pb-20">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Navigation for Desktop */}
          <motion.aside 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="hidden md:block md:w-64 lg:w-72 shrink-0"
          >
            <div className="sticky top-8">
              <nav className="space-y-1 backdrop-blur-md bg-white/70 rounded-2xl p-3 shadow-sm border border-gray-100">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                      activeSection === section.id 
                        ? `${section.color} text-white shadow-sm` 
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <span className={`p-1.5 rounded-md ${activeSection === section.id ? "bg-white/20" : "bg-gray-100"}`}>
                      <section.icon className="w-5 h-5" />
                    </span>
                    <span className="font-medium">{section.title}</span>
                  </button>
                ))}
              </nav>

              <div className="mt-6 bg-linear-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-100">
                <h3 className="font-medium text-gray-900 mb-2">Cookie Preferences</h3>
                <p className="text-sm text-gray-700 mb-3">
                  You can adjust your cookie preferences at any time through our settings panel.
                </p>
                <button className="text-sm bg-amber-600 text-white py-2 px-4 rounded-lg hover:bg-amber-700 transition-colors w-full">
                  Manage Cookies
                </button>
              </div>
            </div>
          </motion.aside>

          {/* Mobile Navigation - Pills */}
          <div className="md:hidden sticky top-0 z-10 pt-3 pb-4 bg-gray-50/80 backdrop-blur-sm">
            <div className="overflow-x-auto flex gap-2 p-1 snap-x">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full whitespace-nowrap shrink-0 transition-colors snap-start ${
                    activeSection === section.id 
                      ? `${section.color} text-white` 
                      : "bg-white text-gray-800 shadow-sm"
                  }`}
                >
                  <section.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{section.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="grow max-w-3xl">
            {sections.map((section) => (
              <motion.section
                key={section.id}
                id={section.id}
                ref={(el) => { if (el) { sectionRefs.current[section.id] = el; } }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="mb-16 scroll-mt-20"
              >
                <div className={`relative ${section.color} text-white py-4 px-6 rounded-xl shadow-md mb-6`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <section.icon className="w-5 h-5" />
                    </div>
                    <h2 className="text-2xl font-bold">{section.title}</h2>
                  </div>
                </div>
                
                <div className="px-2">
                  {section.content}
                </div>

                {section.id !== sections[sections.length - 1].id && (
                  <div className="flex justify-end mt-8">
                    <button 
                      onClick={() => scrollToSection(sections[sections.findIndex(s => s.id === section.id) + 1].id)}
                      className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-800"
                    >
                      <span>Next section</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </motion.section>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
} 